import { useEffect, useRef, useState } from 'react';
import type { ExperienceEntryDetail, ExperienceEntrySummary, ExperienceLibrary, ExperienceLibraryList } from '@aimo/contracts/web';
import type { ApiClient } from '../../api.js';
import { evidenceLabel, filterEntries, kindLabel, orderLibraries, stateLabel } from '../../experience-libraries.js';
import { relativeTime } from '../../stitch-landing.js';
import { Button } from '../button.js';
import { Dialog, DialogContent } from '../dialog.js';
import { Icon } from '../icon.js';
import { Switch } from '../switch.js';
import { cn } from '../utils.js';
import { text } from './messages.js';

/**
 * The home page "Libraries" section (aimo-automatic-experience-libraries D7): the shared experience library and one
 * library per project, on the same showcase cards as the projects, and a browsing dialog with search, entry detail,
 * enable/disable and archive/restore. This is the only management surface; entries are captured automatically.
 */
const focusRing = 'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
const showcaseGrid = 'grid grid-cols-2 gap-4 md:grid-cols-4';
/** The shared library's name is a server default, so it follows the interface language; project libraries keep their project name. */
const libraryName = (library: ExperienceLibrary): string => library.kind === 'shared' ? text.sharedLibrary : library.name;
const failureMessage = (error: unknown): string => error instanceof Error && error.message ? error.message : text.loadFailed;

export function LibrariesSection({ api }: { api: ApiClient }) {
  const [list, setList] = useState<ExperienceLibraryList>();
  const [error, setError] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const epoch = useRef(0);
  const load = () => {
    const current = ++epoch.current;
    api.request<ExperienceLibraryList>('listExperienceLibraries').then(value => { if (current === epoch.current) { setList(value); setError(''); } }, failure => { if (current === epoch.current) setError(failureMessage(failure)); });
  };
  useEffect(() => { load(); return () => { epoch.current++; }; }, [api]);
  const libraries = list ? orderLibraries(list.libraries) : [];
  return (
    <section aria-labelledby="aimo-libraries-heading" className="flex flex-col gap-4" data-testid="experience-libraries">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="aimo-libraries-heading" className="m-0 font-heading text-xl font-medium tracking-tight text-foreground">{text.libraries}</h2>
        <span className="text-xs text-muted-foreground">{list ? (list.autoCapture ? text.autoCaptureOn : text.autoCaptureOff) : text.librariesHint}</span>
      </div>
      {error && <p role="alert" className="m-0 text-[13px] text-(--md-sys-color-error)">{error}</p>}
      {list && (
        <div className={showcaseGrid}>
          {libraries.map(library => <LibraryCard key={library.libraryId} library={library} onOpen={() => setOpen(library.libraryId)} />)}
        </div>
      )}
      <Dialog open={open !== null} onOpenChange={value => { if (!value) { setOpen(null); load(); } }}>
        {open !== null && list && (
          <DialogContent title={text.browseLibraries} size="lg" className="h-[min(720px,calc(100vh-48px))]">
            <LibraryBrowser api={api} libraries={libraries} initial={open} onLibrariesChange={setList} />
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}

/** A library on the same portrait showcase card as a project: the book mark floats over a quiet field, the name sits on the scrim. */
function LibraryCard({ library, onOpen }: { library: ExperienceLibrary; onOpen(): void }) {
  const updated = library.updatedAt ? relativeTime(library.updatedAt) : '';
  const meta = [text.entryCount(library.entryCount), updated, !library.enabled ? text.libraryOff : ''].filter(Boolean).join(' · ');
  return (
    <button type="button" onClick={onOpen} aria-label={text.browseLibrary(libraryName(library))} title={libraryName(library)} data-library-id={library.libraryId}
      className={cn('group relative block aspect-[4/5] w-full cursor-pointer overflow-hidden rounded-2xl border-0 bg-linear-to-b from-(--md-sys-color-surface-container-high) to-(--md-sys-color-surface-container-highest) p-0 text-left font-sans shadow-sm',
        'transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-lg motion-reduce:transition-none motion-reduce:hover:translate-y-0', !library.enabled && 'opacity-70', focusRing)}>
      <span aria-hidden className="absolute inset-x-[9%] bottom-[26%] top-[12%] flex items-center justify-center transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100">
        <span className="flex aspect-[8/5] w-full items-center justify-center rounded-lg bg-card/60 text-(--md-sys-color-outline) shadow-lg ring-1 ring-(--md-sys-color-scrim)/5 backdrop-blur-md">
          <Icon name={library.kind === 'shared' ? 'auto_stories' : 'menu_book'} size={32} />
        </span>
      </span>
      <span aria-hidden className="absolute inset-x-0 bottom-0 h-[42%] bg-linear-to-t from-(--md-sys-color-scrim)/70 via-(--md-sys-color-scrim)/20 to-transparent" />
      <span className="absolute inset-x-4 bottom-3.5 flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-(--md-sys-color-inverse-on-surface)">{libraryName(library)}</span>
        <span className="truncate text-xs text-(--md-sys-color-inverse-on-surface)/70">{meta}</span>
      </span>
    </button>
  );
}

function LibraryBrowser({ api, libraries, initial, onLibrariesChange }: { api: ApiClient; libraries: ExperienceLibrary[]; initial: string; onLibrariesChange(list: ExperienceLibraryList): void }) {
  const [selected, setSelected] = useState(initial);
  const [items, setItems] = useState(libraries);
  const [query, setQuery] = useState('');
  const [archived, setArchived] = useState(false);
  const [entries, setEntries] = useState<ExperienceEntrySummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<ExperienceEntryDetail>();
  const [busy, setBusy] = useState(false);
  const epoch = useRef(0);
  const library = items.find(value => value.libraryId === selected) ?? items[0];
  const loadEntries = () => {
    if (!library) return;
    const current = ++epoch.current; setLoading(true); setError('');
    api.request<{ entries: ExperienceEntrySummary[] }>('listExperienceEntries', { params: { id: library.libraryId }, query: { ...(query.trim() ? { query: query.trim() } : {}), state: archived ? 'archived' : 'published' } })
      .then(value => { if (current === epoch.current) setEntries(value.entries); }, failure => { if (current === epoch.current) setError(failureMessage(failure)); })
      .finally(() => { if (current === epoch.current) setLoading(false); });
  };
  useEffect(() => { setDetail(undefined); loadEntries(); return () => { epoch.current++; }; }, [library?.libraryId, archived]);
  const refreshLibraries = () => api.request<ExperienceLibraryList>('listExperienceLibraries').then(value => { setItems(orderLibraries(value.libraries)); onLibrariesChange(value); }).catch(() => undefined);
  const toggle = (value: ExperienceLibrary, enabled: boolean) => {
    setBusy(true);
    api.request<ExperienceLibrary>('updateExperienceLibrary', { params: { id: value.libraryId }, body: { enabled } })
      .then(updated => { setItems(current => current.map(item => item.libraryId === updated.libraryId ? updated : item)); return refreshLibraries(); }, failure => setError(failureMessage(failure)))
      .finally(() => setBusy(false));
  };
  const openEntry = (entry: ExperienceEntrySummary) => {
    setBusy(true); setError('');
    api.request<ExperienceEntryDetail>('getExperienceEntry', { params: { id: entry.entryId }, query: { library: entry.libraryId } })
      .then(setDetail, failure => setError(failureMessage(failure))).finally(() => setBusy(false));
  };
  const lifecycle = (value: ExperienceEntryDetail, action: 'archive' | 'restore') => {
    setBusy(true); setError('');
    api.request<ExperienceEntryDetail>('changeExperienceEntry', { params: { id: value.entry.entryId }, body: { libraryId: value.entry.libraryId, action, expectedRevision: value.entry.revision } })
      .then(updated => { setDetail(updated); loadEntries(); return refreshLibraries(); }, failure => setError(failureMessage(failure))).finally(() => setBusy(false));
  };
  const visible = filterEntries(entries, query);
  return (
    <div className="flex h-full min-h-0 gap-4">
      <nav aria-label={text.libraryList} className="flex w-[220px] shrink-0 flex-col gap-1 overflow-y-auto rounded-2xl bg-(--md-sys-color-surface-container-low) p-2">
        {items.map(value => (
          <button key={value.libraryId} type="button" aria-pressed={value.libraryId === library?.libraryId} onClick={() => setSelected(value.libraryId)} title={libraryName(value)}
            className={cn('flex cursor-pointer items-center gap-2 rounded-xl border-0 px-3 py-2 text-left font-sans text-[13px]', focusRing, value.libraryId === library?.libraryId ? 'bg-card font-medium text-foreground shadow-sm' : 'bg-transparent text-muted-foreground hover:text-foreground')}>
            <Icon name={value.kind === 'shared' ? 'auto_stories' : 'menu_book'} size={16} />
            <span className="min-w-0 flex-1 truncate">{libraryName(value)}</span>
            <span className="text-[11px] text-muted-foreground">{value.entryCount}</span>
          </button>
        ))}
      </nav>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        {library && (
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-col">
              <h3 className="m-0 truncate font-heading text-[15px] font-medium text-foreground">{libraryName(library)}</h3>
              <span className="text-[11px] text-muted-foreground">{[text.entryCount(library.entryCount), library.updatedAt ? text.updatedAt(relativeTime(library.updatedAt)) : ''].filter(Boolean).join(' · ')}</span>
            </div>
            <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Switch checked={library.enabled} disabled={busy} onCheckedChange={checked => toggle(library, checked)} aria-label={text.enableLibrary} />
              {text.enableLibrary}
            </label>
          </header>
        )}
        {error && <p role="alert" className="m-0 text-[13px] text-(--md-sys-color-error)">{error}</p>}
        {detail
          ? <EntryDetail detail={detail} busy={busy} onBack={() => setDetail(undefined)} onLifecycle={lifecycle} />
          : (
            <>
              <div className="flex items-center gap-2">
                <label className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full border border-transparent bg-(--md-sys-color-surface-container-high) px-3 text-muted-foreground focus-within:border-(--md-sys-color-outline-variant) focus-within:bg-card">
                  <Icon name="search" size={18} />
                  <input type="search" placeholder={text.searchEntries} aria-label={text.searchEntries} value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); loadEntries(); } }}
                    className="min-w-0 flex-1 border-0 bg-transparent font-sans text-[13px] text-foreground outline-none placeholder:text-muted-foreground" />
                </label>
                <label className="flex items-center gap-2 text-[12px] text-muted-foreground"><Switch checked={archived} onCheckedChange={setArchived} aria-label={text.showArchived} />{text.showArchived}</label>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {loading && <p role="status" className="m-0 px-2 py-6 text-center text-[13px] text-muted-foreground">…</p>}
                {!loading && visible.length === 0 && <p className="m-0 px-2 py-8 text-center text-[13px] text-muted-foreground">{text.noEntries}</p>}
                {!loading && visible.length > 0 && (
                  <ul aria-label={text.entries} className="m-0 flex list-none flex-col gap-1 p-0">
                    {visible.map(entry => (
                      <li key={entry.entryId}>
                        <button type="button" onClick={() => openEntry(entry)} data-entry-id={entry.entryId}
                          className={cn('flex w-full cursor-pointer flex-col gap-1 rounded-xl border-0 bg-transparent px-3 py-2 text-left font-sans hover:bg-accent', focusRing)}>
                          <span className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">{entry.title}</span>
                            <EvidenceTag level={entry.evidenceLevel} />
                          </span>
                          <span className="line-clamp-2 text-[12px] text-muted-foreground">{entry.summary}</span>
                          <span className="text-[11px] text-muted-foreground">{[kindLabel(entry.kind), stateLabel(entry.state), relativeTime(entry.updatedAt), entry.recalledCount ? `${text.recalledIn} ${entry.recalledCount}` : ''].filter(Boolean).join(' · ')}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
      </div>
    </div>
  );
}

function EvidenceTag({ level }: { level: ExperienceEntrySummary['evidenceLevel'] }) {
  return (
    <span data-evidence={level} className={cn('inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px]', level === 'verified' ? 'bg-(--md-sys-color-secondary-container) text-(--md-sys-color-on-secondary-container)' : 'bg-(--md-sys-color-surface-container-highest) text-(--md-sys-color-on-surface-variant)')}>
      {level === 'verified' && <Icon name="verified" size={12} />}{evidenceLabel(level)}
    </span>
  );
}

function EntryDetail({ detail, busy, onBack, onLifecycle }: { detail: ExperienceEntryDetail; busy: boolean; onBack(): void; onLifecycle(detail: ExperienceEntryDetail, action: 'archive' | 'restore'): void }) {
  const { entry } = detail;
  const section = (title: string, body: React.ReactNode) => <section className="flex flex-col gap-1"><h4 className="m-0 text-[12px] font-medium text-muted-foreground">{title}</h4>{body}</section>;
  return (
    <article aria-label={entry.title} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1" data-testid="experience-entry-detail">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>← {text.back}</Button>
        <span className="flex-1" />
        {entry.state === 'archived'
          ? <Button variant="outline" size="sm" disabled={busy} onClick={() => onLifecycle(detail, 'restore')}><Icon name="unarchive" size={14} />{text.restoreEntry}</Button>
          : (entry.state === 'published' || entry.state === 'stale') && <Button variant="outline" size="sm" disabled={busy} onClick={() => onLifecycle(detail, 'archive')}><Icon name="archive" size={14} />{text.archiveEntry}</Button>}
      </div>
      <header className="flex flex-col gap-1">
        <h3 className="m-0 font-heading text-[16px] font-medium text-foreground">{entry.title}</h3>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <EvidenceTag level={entry.evidenceLevel} /><span>{kindLabel(entry.kind)}</span><span>{stateLabel(entry.state)}</span><span>r{entry.revision}</span><span>{text.updatedAt(relativeTime(entry.updatedAt))}</span>
          {entry.originProjectId && <span>· {entry.originProjectId.slice(0, 8)}</span>}
        </div>
        {detail.supersededBy && <p className="m-0 text-[12px] text-muted-foreground">{text.supersededBy}</p>}
      </header>
      <p className="m-0 text-[13px] leading-relaxed text-foreground">{entry.summary}</p>
      {detail.detail && detail.detail !== entry.summary && section(text.detail, <pre className="m-0 whitespace-pre-wrap font-sans text-[12px] leading-relaxed text-foreground">{detail.detail}</pre>)}
      {detail.facts.length > 0 && section(text.facts, <ul className="m-0 list-disc pl-5 text-[12px] text-foreground">{detail.facts.map((fact, index) => <li key={index}>{fact}</li>)}</ul>)}
      {section(text.sources, <ul className="m-0 list-none p-0 text-[12px] text-foreground" data-testid="experience-entry-sources">{detail.sources.map(source => <li key={source.runId + source.trigger} data-run-id={source.runId}>{text.sourceRun(source.runId, source.trigger)} · {text.artifacts(source.artifactRefs.length)} · {relativeTime(source.capturedAt)}</li>)}</ul>)}
      {section(text.versions, <ol className="m-0 list-none p-0 text-[12px] text-foreground" data-testid="experience-entry-versions">{detail.versions.map(version => <li key={version.revision}><Icon name="history" size={12} /> r{version.revision} · {stateLabel(version.state)} · {version.reason || version.actor} · {relativeTime(version.at)}</li>)}</ol>)}
      {section(text.recalledIn, detail.recalledIn.length
        ? <ul className="m-0 list-none p-0 text-[12px] text-foreground" data-testid="experience-entry-recalled">{detail.recalledIn.map(recall => <li key={recall.runId} data-run-id={recall.runId}>{text.sourceRun(recall.runId, 'recall').split(' · ')[0]} · {relativeTime(recall.at)}</li>)}</ul>
        : <p className="m-0 text-[12px] text-muted-foreground">{text.neverRecalled}</p>)}
    </article>
  );
}
